import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from './AuthContext';

const SchoolContext = createContext();

export function SchoolProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [school, setSchool] = useState(null);
  const [sections, setSections] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSchoolData = useCallback(async (schoolId, ownerId = user?.id) => {
    try {
      setError('');

      let schoolQuery = supabase
        .from('schools')
        .select('*')
        .eq('id', schoolId);

      if (ownerId) {
        schoolQuery = schoolQuery.eq('owner_id', ownerId);
      }

      const { data: schoolData, error: schoolError } = await schoolQuery.limit(1).maybeSingle();

      if (schoolError) throw schoolError;
      if (!schoolData) {
        throw new Error('School account was not found.');
      }

      const { data: sectionsData, error: sectionsError } = await supabase
        .from('sections')
        .select('*')
        .eq('school_id', schoolId)
        .order('created_at', { ascending: true });

      if (sectionsError) throw sectionsError;

      const { terms: loadedTerms, currentTerm, school: nextSchool } = await loadTermsForSchool(schoolData);

      setSchool(nextSchool);
      localStorage.setItem('schoolId', schoolData.id);
      localStorage.setItem('schoolName', schoolData.name);

      setSections(sectionsData || []);
      localStorage.setItem('sections', JSON.stringify(sectionsData || []));
      setTerms(loadedTerms);
      setSelectedTerm(currentTerm);
    } catch (err) {
      console.error(err);
      setError('Could not load the latest school data. Showing saved setup data if available.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchSchoolForUser = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError('');

      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .select('*')
        .eq('owner_id', userId)
        .limit(1)
        .maybeSingle();

      if (schoolError) throw schoolError;
      if (!schoolData) {
        throw new Error('No school account was found for this login.');
      }

      await fetchSchoolData(schoolData.id, userId);
    } catch (err) {
      console.error(err);
      setError('Could not load your school account.');
      setLoading(false);
    }
  }, [fetchSchoolData]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setSchool(null);
      setSections([]);
      setTerms([]);
      setSelectedTerm(null);
      setLoading(false);
      return;
    }

    const schoolId = localStorage.getItem('schoolId');
    const schoolName = localStorage.getItem('schoolName');
    const cachedSections = getCachedSections();

    if (schoolName) {
      setSchool({ id: schoolId, name: schoolName });
    }

    if (cachedSections.length > 0) {
      setSections(cachedSections);
    }

    fetchSchoolForUser(user.id);
  }, [user, authLoading, fetchSchoolForUser]);

  const addSection = async (name) => {
    const schoolId = localStorage.getItem('schoolId') || school?.id || sections[0]?.school_id;
    if (!schoolId) {
      throw new Error('School ID is missing.');
    }

    localStorage.setItem('schoolId', schoolId);

    const initials = getSectionInitials(name, sections);
    const { data, error: insertError } = await supabase
      .from('sections')
      .insert({
        school_id: schoolId,
        name: name.trim(),
        initials,
        fields: [],
      })
      .select()
      .single();

    if (insertError) throw insertError;

    const nextSections = [...sections, data];
    setSections(nextSections);
    localStorage.setItem('sections', JSON.stringify(nextSections));

    return data;
  };

  const updateSectionFields = async (sectionId, fields) => {
    const { data, error: updateError } = await supabase
      .from('sections')
      .update({ fields })
      .eq('id', sectionId)
      .select()
      .single();

    if (updateError) throw updateError;

    const nextSections = sections.map(section =>
      section.id === sectionId ? data : section
    );

    setSections(nextSections);
    localStorage.setItem('sections', JSON.stringify(nextSections));

    return data;
  };

  const syncSections = (nextSections) => {
    setSections(nextSections || []);
    localStorage.setItem('sections', JSON.stringify(nextSections || []));
  };

  const selectTerm = (termId) => {
    const nextTerm = terms.find(term => term.id === termId);
    if (nextTerm) {
      setSelectedTerm(nextTerm);
    }
  };

  const startNextTerm = async () => {
    const schoolId = school?.id || localStorage.getItem('schoolId');
    const currentTerm = terms.find(term => term.id === school?.current_term_id) || selectedTerm;

    if (!schoolId || !currentTerm) {
      throw new Error('Current term information is missing.');
    }

    const nextTermDetails = getNextTermDetails(currentTerm);

    const { error: archiveError } = await supabase
      .from('terms')
      .update({
        status: 'archived',
        ends_at: new Date().toISOString().slice(0, 10),
      })
      .eq('id', currentTerm.id);

    if (archiveError) throw archiveError;

    const { data: newTerm, error: termError } = await supabase
      .from('terms')
      .insert({
        school_id: schoolId,
        name: nextTermDetails.name,
        academic_year: nextTermDetails.academicYear,
        status: 'active',
        starts_at: new Date().toISOString().slice(0, 10),
      })
      .select()
      .single();

    if (termError) throw termError;

    const { data: updatedSchool, error: schoolError } = await supabase
      .from('schools')
      .update({ current_term_id: newTerm.id })
      .eq('id', schoolId)
      .select()
      .single();

    if (schoolError) throw schoolError;

    const nextTerms = [
      ...terms.map(term => (
        term.id === currentTerm.id
          ? { ...term, status: 'archived', ends_at: new Date().toISOString().slice(0, 10) }
          : term
      )),
      newTerm,
    ];

    setTerms(sortTerms(nextTerms));
    setSelectedTerm(newTerm);
    setSchool(updatedSchool);

    return newTerm;
  };

  return (
    <SchoolContext.Provider
      value={{
        school,
        sections,
        terms,
        selectedTerm,
        currentTerm: terms.find(term => term.id === school?.current_term_id) || selectedTerm,
        loading,
        error,
        addSection,
        updateSectionFields,
        syncSections,
        selectTerm,
        startNextTerm,
        refreshSchoolData: fetchSchoolData,
      }}
    >
      {children}
    </SchoolContext.Provider>
  );
}

export function useSchool() {
  return useContext(SchoolContext);
}

function getCachedSections() {
  try {
    return JSON.parse(localStorage.getItem('sections') || '[]');
  } catch (err) {
    console.error(err);
    return [];
  }
}

function getSectionInitials(name, existing) {
  const base = name.trim().slice(0, 3).toUpperCase() || 'SEC';
  const clash = existing.some(section => section.initials === base);

  if (!clash) {
    return base;
  }

  return name.trim().slice(0, 4).toUpperCase() || base;
}

async function loadTermsForSchool(schoolData) {
  const { data: termsData, error: termsError } = await supabase
    .from('terms')
    .select('*')
    .eq('school_id', schoolData.id)
    .order('created_at', { ascending: false });

  if (termsError) throw termsError;

  let loadedTerms = termsData || [];
  let nextSchool = schoolData;
  let currentTerm = loadedTerms.find(term => term.id === schoolData.current_term_id)
    || loadedTerms.find(term => term.status === 'active')
    || loadedTerms[0];

  if (!currentTerm) {
    const { data: createdTerm, error: createTermError } = await supabase
      .from('terms')
      .insert({
        school_id: schoolData.id,
        name: 'Third Term',
        academic_year: '2025/2026',
        status: 'active',
      })
      .select()
      .single();

    if (createTermError) throw createTermError;

    currentTerm = createdTerm;
    loadedTerms = [createdTerm];
  }

  if (nextSchool.current_term_id !== currentTerm.id) {
    const { data: updatedSchool, error: updateSchoolError } = await supabase
      .from('schools')
      .update({ current_term_id: currentTerm.id })
      .eq('id', schoolData.id)
      .select()
      .single();

    if (updateSchoolError) throw updateSchoolError;
    nextSchool = updatedSchool;
  }

  return {
    school: nextSchool,
    terms: sortTerms(loadedTerms),
    currentTerm,
  };
}

function sortTerms(termList) {
  return [...termList].sort((a, b) => (
    new Date(b.created_at || 0) - new Date(a.created_at || 0)
  ));
}

function getNextTermDetails(term) {
  if (term.name === 'First Term') {
    return { name: 'Second Term', academicYear: term.academic_year };
  }

  if (term.name === 'Second Term') {
    return { name: 'Third Term', academicYear: term.academic_year };
  }

  return {
    name: 'First Term',
    academicYear: getNextAcademicYear(term.academic_year),
  };
}

function getNextAcademicYear(academicYear) {
  const [start, end] = String(academicYear || '').split('/').map(value => Number(value));

  if (!start || !end) {
    return '2026/2027';
  }

  return `${start + 1}/${end + 1}`;
}
